import StatusCode from 'status-code-enum';
import getConfig from 'next/config';
import { isAxiosError } from 'axios';

import { logger } from '@dist-marketplace-frontend/services';

import { adViewApi } from '../../../../api/adViewApi';
import { fireToast } from '../../../../helpers/Toasts';
import { ShowPhoneNumberButton } from '../../../../components/Toolkit/ShowPhoneNumberButton/ShowPhoneNumberButton';
import { pushToDataLayer } from '../../../../services/gtmService';

import { useScript } from '../../../../hooks/UseScript';
import { getRecaptchaToken } from '../../../../helpers/reCaptcha';
import { useBraze } from '../../../../hooks/useBraze';
import { useAuthOptions } from '../../../../contexts/UserContext';
import type { SellerTypes } from '../../../../components/PageWrappers/AdDetails/types';
import { PAGE } from '../../../../helpers/pages';

const {
  publicRuntimeConfig: { GOOGLE_RECAPTCHA },
} = getConfig();

interface RevealPhoneNumberProps {
  adId: number;
  showNumberTextVariant?: boolean;
  size?: 'SMALL' | 'LARGE' | 'GROW';
  trackingId?: string;
  clickSource?: string;
  isPrimaryButton?: boolean;
  campaignId?: string | null;
  disabled?: boolean;
  sectionName?: string;
  sellerType?: SellerTypes;
  id?: string;
}

function RevealPhoneNumber({
  adId,
  showNumberTextVariant = false,
  size = 'SMALL',
  trackingId = 'reveal-number',
  clickSource = '',
  isPrimaryButton = false,
  campaignId,
  sectionName,
  sellerType,
  ...rest
}: RevealPhoneNumberProps) {
  useScript(
    `https://www.google.com/recaptcha/enterprise.js?render=${GOOGLE_RECAPTCHA}`,
    'recaptcha',
    [adId],
  );

  const { handleLogin, accessToken } = useAuthOptions();
  const { logBrazeRevealPhoneEvent } = useBraze();

  async function handleReveal() {
    try {
      const captchaToken = await getRecaptchaToken('phone_reveal');
      const { data } = await adViewApi.getPhoneNumber(
        adId,
        captchaToken,
        campaignId,
        accessToken,
      );

      pushToDataLayer({ event: 'phone_reveal', source: clickSource });
      logBrazeRevealPhoneEvent(sectionName, sellerType);
      if (!data.valid) {
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
        return false;
      }
      return data.displayFormat;
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.status === StatusCode.ClientErrorUnauthorized
      ) {
        handleLogin();
      } else {
        logger.error({
          error,
          tags: [PAGE.AD_DETAILS],
          message: 'Error revealing phone number',
        });
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
      return false;
    }
  }

  async function handleOnCall() {
    try {
      pushToDataLayer({ event: 'click_to_call', source: clickSource });
      await adViewApi.trackAdCallNumber(adId, campaignId);
    } catch (error) {
      logger.error({
        error,
        message: 'Error tracking call to phone number',
        tags: [PAGE.AD_DETAILS],
      });
    }
  }

  return (
    <ShowPhoneNumberButton
      adId={adId}
      onReveal={handleReveal}
      showNumberTextVariant={showNumberTextVariant}
      size={size}
      trackingId={trackingId}
      onCall={handleOnCall}
      isPrimaryButton={isPrimaryButton}
      {...rest}
    />
  );
}

export { RevealPhoneNumber, type RevealPhoneNumberProps };
